﻿import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { useCalendarPermissions } from "@/shared/hooks/useCalendarPermissions";
import {
	billetStatus,
	BilletStatusSelector,
} from "@/shared/selectors/BilletStatusSelector";
import { backgroundColor } from "@/utils/theme";
import {
	dxtToLocalServerTime,
	ErrorModal,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	ClickAwayListener,
	FormControlLabel,
	Grid2,
	Input,
	lighten,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCalendarAreaId } from "../context/CalendarAreaContext";
import { useCalendarOptions } from "../hooks/useCalendarOptions";
import { useCustomTableColumns } from "../hooks/useCustomTableColumns";
import { useSearchEvents } from "../hooks/useSearchEvents";
import { useTableCellEditing } from "../hooks/useTableCellEditing";
import { useTableDragDrop } from "../hooks/useTableDragDrop";
import { Event } from "../models/Event";
import { ColumnDef, MoveEventData, ProcessedRow } from "../models/utils";
import {
	upsertEvent,
	upsertFieldProductionEvent,
} from "../repositories/CalendarRepository";
import {
	buildHeaderGroups,
	calculateColumnWidths,
	calculateRowSpans,
	filterProcessedData,
	isLastRowForEvent,
	processEventsToRows,
} from "../utils/tableUtils";
import { DragModeSnackbar } from "./DragModeSnackbar";
import { ConfirmMoveEventModal } from "./modals/ConfirmMoveEventModal";
import { NewEventModal } from "./modals/NewEventModal";

// Toggle between rowSpan or showing values only on first row
const SHOW_VALUES_ON_FIRST_ROW = false;

interface CustomEventsTableProps {
	month: number;
	year: number;
	onEventClick: (eventId: number) => void;
	onDelete: (eventId: number) => void;
	onSuccess: (message?: string) => void;
	refetch: () => void;
	setOpenConfirmModal: (id: number) => void;
	isDraftMode: boolean;
}

export const CustomEventsTable = ({
	month,
	year,
	onEventClick,
	onDelete,
	onSuccess,
	refetch,
	setOpenConfirmModal,
	isDraftMode,
}: CustomEventsTableProps) => {
	const theme = useTheme();
	const defaultAreaId = useCalendarAreaId();
	const { canEditSchedule, canAddDownDay } = useCalendarPermissions();

	const [searchParams] = useSearchParams();
	const highlightedEventIdParam = Number(searchParams.get("eventId"));

	const tableContainerRef = useRef<HTMLDivElement>(null);
	const isDraftModeRef = useRef(isDraftMode);
	isDraftModeRef.current = isDraftMode;

	const [filterValue, setFilterValue] = useState("");
	const [useColors, setUseColors] = useState(false);
	const [hideOtherEvents, setHideOtherEvents] = useState(true);
	const [error, setError] = useState("");
	const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
	const [subtitleNewEvent, setSubtitleNewEvent] = useState("");
	const [newEventDates, setNewEventDates] = useState<{
		start: Date;
		end: Date | null;
	} | null>(null);
	const [hoveredBilletStatusCode, setHoveredBilletStatusCode] = useState<
		string | null
	>(null);
	const [hoveredEventTypeCode] = useState<string | null>(null);
	const [sectionMenuPosition, setSectionMenuPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [selectedSectionEvent, setSelectedSectionEvent] =
		useState<ProcessedRow | null>(null);
	const [confirmMoveModalData, setConfirmMoveModalData] =
		useState<MoveEventData | null>(null);

	const firstDayOfMonth = new Date(year, month, 1, 0, 0, 0);
	const lastDayOfMonth = new Date(year, month + 1, 0, 23, 59, 59);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchEvents({
		start: firstDayOfMonth,
		end: lastDayOfMonth,
		stopRefetch: createEventModalOpen,
		isDraftMode,
	});

	const { registerConfig, showContextMenu } = useContextMenuMESF();

	const { getMenuOptions } = useCalendarOptions({
		canEdit: canEditSchedule,
		canAdd: canAddDownDay,
		onNew: (startDate, endDate, previousSection, nextSection) => {
			setCreateEventModalOpen(true);
			setNewEventDates({ start: startDate, end: endDate ?? null });
			if (previousSection || nextSection) {
				setSubtitleNewEvent(
					`Creating event ${
						previousSection ? `after section '${previousSection}'` : ""
					}${previousSection && nextSection ? " and " : ""}${
						nextSection ? `before '${nextSection}'` : ""
					}`,
				);
			}
		},
		onEdit: (id: number) => {
			onEventClick(id);
		},
		onDelete: (id: number) => {
			onDelete(id);
		},
		openConfirmModal: (event: Event) => {
			setOpenConfirmModal(event.EventId);
		},
	});

	const { visibleColumns, getRowBackgroundColor } = useCustomTableColumns({
		handleEditClick: (eventId: number) => onEventClick(eventId),
		isDraftMode,
		calendarMonth: month,
		onSectionClick: (
			event: ProcessedRow,
			position: { x: number; y: number },
		) => {
			if (!canEditSchedule) return;
			clearSelection();
			setSelectedSectionEvent(event);
			setSectionMenuPosition(position);
		},
	});

	const processedData = useMemo(
		() => processEventsToRows(data, month, year),
		[data, month, year],
	);
	const filteredData = useMemo(() => {
		const base = hideOtherEvents
			? processedData.filter((row) => row.EventTypeCode !== "O")
			: processedData;
		return filterProcessedData(base, filterValue);
	}, [processedData, filterValue, hideOtherEvents]);
	const rowSpans = useMemo(() => {
		if (SHOW_VALUES_ON_FIRST_ROW) return null;
		return calculateRowSpans(filteredData, visibleColumns);
	}, [filteredData, visibleColumns]);
	const headerGroups = useMemo(
		() => buildHeaderGroups(visibleColumns),
		[visibleColumns],
	);
	const columnWidths = useMemo(
		() => calculateColumnWidths(visibleColumns),
		[visibleColumns],
	);

	const upsertCell = useMutation(upsertFieldProductionEvent, {
		onSuccess: () => {
			onSuccess();
		},
		onError: async (error: Error) => {
			setError(error.message);
			await refetch();
		},
	});
	const queryClient = useQueryClient();
	const moveEvent = useMutation(upsertEvent, {
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["event"] });
			onSuccess("Event moved successfully");
		},
		onError: async (error: Error) => {
			setError(error.message);
			await refetch();
		},
	});

	const {
		draggedEventId,
		dragOverEventId,
		dragPreviewDates,
		dragPreviewPosition,
		dragPreviewAdjacentEvents,
		handleDragStart,
		handleDragOver,
		handleDrop,
		cleanupDrag,
	} = useTableDragDrop({
		filteredData,
		onConfirmMove: setConfirmMoveModalData,
	});

	const {
		editingCell,
		selectedCell,
		handleCellClick,
		handleCellDoubleClick,
		handleCellChange,
		handleCellBlur,
		handleEditKeyDown,
		clearSelection,
	} = useTableCellEditing({
		filteredData,
		visibleColumns,
		onCellUpdate: async (params) => {
			const { row, field, value } = params;
			const isScheduledField = String(field).startsWith("Scheduled");
			const isActualField = String(field).startsWith("Actual");

			const getValue = (f: string) => (field === f ? value : (row as any)[f]);

			let autocalculatedFields: any = {};

			if (isScheduledField) {
				const scheduledRolled = getValue("ScheduledRolledTons");
				const scheduledCharged = getValue("ScheduledChargedTons");
				const scheduledFinished = getValue("ScheduledFinishedTons");
				const budgetTPH = getValue("BudgetTPH");

				autocalculatedFields = {
					status: "Scheduled",
					newRollingHs:
						scheduledRolled && budgetTPH
							? Math.round(scheduledRolled / budgetTPH)
							: 0,
					newMillYield:
						scheduledRolled && scheduledCharged
							? (scheduledRolled / scheduledCharged) * 100
							: 0,
					newFinishedYield:
						scheduledFinished && scheduledCharged
							? (scheduledFinished / scheduledCharged) * 100
							: 0,
				};
			}

			if (isActualField) {
				const actualRolled = getValue("ActualRolledTons");
				const actualCharged = getValue("ActualChargedTons");
				const actualFinished = getValue("ActualFinishedTons");
				const actualTPH = getValue("ActualTPH");

				autocalculatedFields = {
					status: "Actual",
					newRollingHs:
						actualRolled && actualTPH
							? Math.round(actualRolled / actualTPH)
							: 0,
					newMillYield:
						actualRolled && actualCharged
							? (actualRolled / actualCharged) * 100
							: 0,
					newFinishedYield:
						actualFinished && actualCharged
							? (actualFinished / actualCharged) * 100
							: 0,
				};
			}

			await upsertCell.mutateAsync({
				eventId: params.eventId,
				start: params.start,
				end: params.end ?? null,
				sectionId: params.sectionId,
				monthStart: `${year}-${String(month + 1).padStart(2, "0")}-01`,
				field: params.field,
				value: params.value,
				autocalculatedFields,
				isDraftMode: isDraftModeRef.current,
				defaultAreaId,
			});
		},
	});

	const handleConfirmMove = () => {
		if (!confirmMoveModalData) return;

		const { draggedEvent, newStart, newEnd } = confirmMoveModalData;

		moveEvent.mutate({
			eventId: draggedEvent.EventId,
			start: newStart,
			end: newEnd,
			eventTypeCode: draggedEvent.EventTypeCode ?? "P",
			comments: draggedEvent.EventComments ?? null,
			isDraftMode: isDraftModeRef.current,
			defaultAreaId,
		});

		setConfirmMoveModalData(null);
		cleanupDrag();
	};

	const handleCancelMove = () => {
		setConfirmMoveModalData(null);
		cleanupDrag();
	};

	useEffect(() => {
		registerConfig({
			id: "calendarListOptions",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	useEffect(() => {
		if (!selectedCell || !tableContainerRef.current) return;

		const cellElement = tableContainerRef.current.querySelector(
			`[data-cell-id="${selectedCell.rowIndex}-${selectedCell.colIndex}"]`,
		) as HTMLElement;

		if (cellElement) {
			cellElement.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [selectedCell]);

	useEffect(() => {
		if (!highlightedEventIdParam || !tableContainerRef.current || isLoading)
			return;

		const rowElement = tableContainerRef.current.querySelector(
			`[data-event-id="${highlightedEventIdParam}"]`,
		) as HTMLElement | null;

		if (rowElement) {
			rowElement.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [highlightedEventIdParam, isLoading, filteredData]);

	const renderCellContent = (
		row: ProcessedRow,
		col: ColumnDef,
		rowKey: string,
	) => {
		const isEditing =
			editingCell?.rowKey === rowKey && editingCell?.field === col.field;

		if (isEditing) {
			return (
				<ClickAwayListener onClickAway={handleCellBlur}>
					<Input
						autoFocus
						value={editingCell.value}
						onChange={(e) => handleCellChange(e.target.value)}
						onKeyDown={handleEditKeyDown}
						onBlur={handleCellBlur}
						sx={{
							fontSize: 11,
							lineHeight: 1.1,
							width: "100%",
							"& input": {
								textAlign: col.align || "left",
								padding: "0px 4px",
							},
						}}
					/>
				</ClickAwayListener>
			);
		}

		if (SHOW_VALUES_ON_FIRST_ROW && col.spanRows && !row.IsFirstRow) {
			return "";
		}

		if (col.render) {
			const value = col.getValue ? col.getValue(row) : (row as any)[col.field];
			return col.render(row, value);
		}

		let value = col.getValue ? col.getValue(row) : (row as any)[col.field];
		if (col.formatValue && value != null) {
			return col.formatValue(value);
		}

		return value ?? "";
	};

	return (
		<Box onClick={clearSelection} onContextMenu={clearSelection}>
			<Grid2 container spacing={2} alignItems="center">
				<Grid2 size={{ xs: 12, sm: 8 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => setFilterValue(val)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={useColors}
								onChange={(e) => setUseColors(e.target.checked)}
								color="primary"
							/>
						}
						label="Difference sections with colors"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={hideOtherEvents}
								onChange={(e) => setHideOtherEvents(e.target.checked)}
								color="primary"
							/>
						}
						label='Hide "Other" events'
					/>
				</Grid2>
			</Grid2>

			<Box sx={{ width: "100%", marginTop: 1 }}>
				<TableContainer
					ref={tableContainerRef}
					component={Paper}
					sx={{ height: "72vh", overflow: "auto" }}
				>
					<Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
						<colgroup>
							{visibleColumns.map((col, idx) => (
								<col
									key={col.field}
									style={{
										width: columnWidths[idx].width,
										minWidth: col.minWidth,
									}}
								/>
							))}
						</colgroup>
						<TableHead>
							{/* Group headers row */}
							<TableRow>
								{headerGroups.map((group, idx) => {
									const isGrouped = group.colSpan > 1;
									return (
										<TableCell
											key={idx}
											colSpan={group.colSpan}
											rowSpan={isGrouped ? 1 : 2}
											align="center"
											sx={{
												backgroundColor: "#fff",
												fontWeight: 600,
												fontSize: 11,
												lineHeight: 2,
												borderBottom: isGrouped
													? "1px solid rgba(224, 224, 224, 1)"
													: undefined,
												py: 0,
												verticalAlign: "middle",
												position: "sticky",
												top: 0,
												zIndex: 2,
											}}
										>
											{isGrouped
												? group.headerName
												: group.columns[0]?.headerName}
										</TableCell>
									);
								})}
							</TableRow>
							{/* Column headers row - only for grouped columns */}
							<TableRow>
								{visibleColumns
									.filter((col) => col.group)
									.map((col) => (
										<TableCell
											key={col.field}
											align={col.align || "left"}
											sx={{
												fontSize: 11,
												fontWeight: 600,
												lineHeight: 2,
												py: 0,
												boxSizing: "border-box",
												backgroundColor: "#fff",
												position: "sticky",
												top: 22,
												zIndex: 2,
											}}
										>
											{col.headerName}
										</TableCell>
									))}
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={visibleColumns.length} align="center">
										<CenteredLazyLoading />
									</TableCell>
								</TableRow>
							) : filteredData.length === 0 ? (
								<TableRow>
									<TableCell colSpan={visibleColumns.length} align="center">
										No events found
									</TableCell>
								</TableRow>
							) : (
								filteredData.map((row, rowIndex) => {
									const rowKey = `${row.EventId}-${row.RowIndex ?? rowIndex}`;
									const isFirstRow = row.IsFirstRow;
									const isDragging = draggedEventId === row.EventId;
									const isDropTarget =
										dragOverEventId === row.EventId &&
										isLastRowForEvent(row, rowIndex, filteredData);

									const canDrag =
										canEditSchedule &&
										isFirstRow &&
										row.EventId > 0 &&
										row.StatusCode === "P";

									return (
										<TableRow
											data-test-id={`event_${row.EventId}`}
											data-event-id={row.EventId}
											key={rowKey}
											draggable={canDrag}
											onDragStart={(e) =>
												handleDragStart(
													e,
													row.EventId,
													row.OriginalStart,
													row.OriginalEnd,
													row.StatusCode,
												)
											}
											onDragOver={(e) =>
												handleDragOver(
													e,
													row.EventId,
													row.OriginalEnd,
													row.Start,
												)
											}
											onDrop={(e) =>
												handleDrop(e, row.EventId, row.OriginalEnd, row.Start)
											}
											onDragEnd={cleanupDrag}
											onContextMenu={(e) => {
												clearSelection();
												e.preventDefault();
												if (row.IsMTDSummary || row.IsMonthSummary) return;
												if (row.EventId < 0) {
													showContextMenu(
														e as any,
														{
															isEmptySpace: true,
															start: row.Start,
															end: row.End,
														},
														"calendarListOptions",
													);
												} else {
													showContextMenu(e as any, row, "calendarListOptions");
												}
											}}
											sx={{
												backgroundColor:
													highlightedEventIdParam === row.EventId
														? backgroundColor
														: getRowBackgroundColor({
																row,
																hoveredBilletStatusCode,
																hoveredEventTypeCode,
																useColors,
															}),
												opacity: isDragging ? 0.5 : 1,
												cursor: row.EventId > 0 ? "pointer" : "default",
												borderBottom: "1px solid rgba(224, 224, 224, 1)",
												position: "relative",
												height: 15,
												"& td": {
													height: 15,
													fontWeight:
														row.StatusCode !== "P" && row.EventId > 0
															? "bold"
															: "normal",
												},
												"&:hover": {
													backgroundColor:
														row.EventId > 0
															? lighten(
																	getRowBackgroundColor({
																		row,
																		hoveredBilletStatusCode,
																		hoveredEventTypeCode,
																		useColors,
																	}),
																	0.1,
																)
															: getRowBackgroundColor({
																	row,
																	hoveredBilletStatusCode,
																	hoveredEventTypeCode,
																	useColors,
																}),
												},
												...(canDrag && {
													"&:hover .row-drag-handle": {
														opacity: 1,
													},
												}),
												...(isDropTarget && {
													"&::after": {
														content: '""',
														position: "absolute",
														left: 0,
														right: 0,
														bottom: -2,
														height: 4,
														backgroundColor: theme.palette.info.main,
														zIndex: 10,
														boxShadow: `0 0 8px ${theme.palette.info.main}`,
													},
												}),
											}}
										>
											{visibleColumns.map((col, colIndex) => {
												const dataTestId = `${col.field
													.replace(/([A-Z])/g, "_$1")
													.toLowerCase()
													.replace(/^_/, "")}_cell`; // Handle rowSpan logic
												if (!SHOW_VALUES_ON_FIRST_ROW && rowSpans) {
													const cellKey = `${rowIndex}-${col.field}`;
													if (rowSpans.skipRows.has(cellKey)) {
														return null; // Skip this cell, it's merged
													}
													const span = rowSpans.spans.get(cellKey) || 1;

													const isSelected =
														selectedCell?.rowIndex === rowIndex &&
														selectedCell?.colIndex === colIndex;

													return (
														<TableCell
															key={col.field}
															data-cell-id={`${rowIndex}-${colIndex}`}
															rowSpan={
																col.spanRows || col.spanRowsByDay ? span : 1
															}
															align={col.align || "left"}
															onClick={(e) => {
																e.stopPropagation();
																if (!canEditSchedule) return;
																handleCellClick(
																	row,
																	col,
																	rowKey,
																	rowIndex,
																	colIndex,
																);
															}}
															onDoubleClick={(e) => {
																e.stopPropagation();
																if (!canEditSchedule) return;
																handleCellDoubleClick(row, col, rowKey);
															}}
															sx={{
																width: columnWidths[colIndex].width,
																minWidth: col.minWidth,
																fontSize: 11,
																lineHeight: 1,
																py: 0,
																px: 0.5,
																boxSizing: "border-box",
																...col.cellStyle?.(row),
																cursor:
																	typeof col.editable === "function"
																		? col.editable(row)
																			? "cell"
																			: "default"
																		: col.editable
																			? "cell"
																			: "default",
																...(isSelected && {
																	outline: `2px solid ${theme.palette.primary.main}`,
																	outlineOffset: -2,
																}),
															}}
															data-test-id={dataTestId}
														>
															{renderCellContent(row, col, rowKey)}
														</TableCell>
													);
												}

												// SHOW_VALUES_ON_FIRST_ROW mode
												const isSelectedAlt =
													selectedCell?.rowIndex === rowIndex &&
													selectedCell?.colIndex === colIndex;

												return (
													<TableCell
														key={col.field}
														data-cell-id={`${rowIndex}-${colIndex}`}
														align={col.align || "left"}
														onClick={(e) => {
															e.stopPropagation();
															if (!canEditSchedule) return;
															handleCellClick(
																row,
																col,
																rowKey,
																rowIndex,
																colIndex,
															);
														}}
														onDoubleClick={(e) => {
															e.stopPropagation();
															if (!canEditSchedule) return;
															handleCellDoubleClick(row, col, rowKey);
														}}
														sx={{
															width: columnWidths[colIndex].width,
															minWidth: col.minWidth,
															fontSize: 11,
															lineHeight: 1,
															py: 0,
															px: 0.5,
															boxSizing: "border-box",
															...col.cellStyle?.(row),
															cursor:
																typeof col.editable === "function"
																	? col.editable(row)
																		? "cell"
																		: "default"
																	: col.editable
																		? "cell"
																		: "default",
															...(isSelectedAlt && {
																outline: `2px solid ${theme.palette.primary.main}`,
																outlineOffset: -2,
															}),
														}}
														data-test-id={dataTestId}
													>
														{renderCellContent(row, col, rowKey)}
													</TableCell>
												);
											})}
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{/* Legend */}
				<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
					<Box sx={{ display: "flex", gap: 1 }}>
						{billetStatus.map((type) => (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									cursor: "pointer",
									px: 1,
									py: 0.5,
									borderRadius: 1,
									backgroundColor:
										hoveredBilletStatusCode === type.code
											? "#e0e0e0"
											: "#fcfcfcff",
									transition: "background-color 0.2s",
								}}
								key={type.code}
								onMouseEnter={() => setHoveredBilletStatusCode(type.code)}
								onMouseLeave={() => setHoveredBilletStatusCode(null)}
							>
								<Box
									sx={{
										width: 15,
										height: 15,
										borderRadius: 4,
										backgroundColor: type.backgroundColor,
									}}
								/>
								<Typography variant="body2" color="textSecondary">
									Billets {type.name}
								</Typography>
							</Box>
						))}
					</Box>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Typography variant="body2" color="textSecondary">
							Scheduled: monthly prorated (event total)
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Drag preview tooltip */}
			{dragPreviewDates && dragPreviewPosition && (
				<Box
					sx={{
						position: "fixed",
						left: `${dragPreviewPosition.x}px`,
						top: `${dragPreviewPosition.y}px`,
						backgroundColor: theme.palette.info.main,
						color: theme.palette.info.contrastText,
						padding: "8px 12px",
						borderRadius: "6px",
						fontSize: "0.875rem",
						boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
						zIndex: 10000,
						pointerEvents: "none",
						fontWeight: 600,
						whiteSpace: "nowrap",
					}}
				>
					<Box>
						New: {dxtToLocalServerTime(dragPreviewDates.start, "MM/dd HH:mm")} -{" "}
						{dxtToLocalServerTime(dragPreviewDates.end, "MM/dd HH:mm")}
					</Box>
					{dragPreviewAdjacentEvents && (
						<Box
							sx={{
								fontSize: "0.75rem",
								fontWeight: 400,
								mt: 0.5,
								opacity: 0.9,
							}}
						>
							{dragPreviewAdjacentEvents.eventBefore && (
								<span>
									After:{" "}
									{dragPreviewAdjacentEvents.eventBefore.SectionName ??
										dragPreviewAdjacentEvents.eventBefore.EventTypeName}
								</span>
							)}
							{dragPreviewAdjacentEvents.eventBefore &&
								dragPreviewAdjacentEvents.eventAfter &&
								" | "}
							{dragPreviewAdjacentEvents.eventAfter && (
								<span>
									Before:{" "}
									{dragPreviewAdjacentEvents.eventAfter.SectionName ??
										dragPreviewAdjacentEvents.eventAfter.EventTypeName}
								</span>
							)}
						</Box>
					)}
				</Box>
			)}

			<DragModeSnackbar visible={draggedEventId !== null} />

			<NewEventModal
				subtitle={subtitleNewEvent}
				open={createEventModalOpen}
				onHide={async (success: boolean) => {
					setCreateEventModalOpen(false);
					setNewEventDates(null);

					if (success) {
						onSuccess("Event created successfully");
					}
				}}
				newEventDates={newEventDates}
				isDraftMode={isDraftMode}
				restrictToEventType={canEditSchedule ? undefined : "D"}
			/>

			<ConfirmMoveEventModal
				open={confirmMoveModalData !== null}
				loading={moveEvent.isPending}
				moveData={confirmMoveModalData}
				onConfirm={handleConfirmMove}
				onCancel={handleCancelMove}
			/>

			<Popover
				open={!!sectionMenuPosition}
				anchorPosition={
					sectionMenuPosition
						? { left: sectionMenuPosition.x, top: sectionMenuPosition.y }
						: undefined
				}
				anchorReference="anchorPosition"
				onClose={() => {
					setSectionMenuPosition(null);
					setSelectedSectionEvent(null);
				}}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<Box sx={{ p: 2, minWidth: 250 }}>
					<BilletStatusSelector
						label="Billet Status"
						value={selectedSectionEvent?.BilletsStatusCode ?? null}
						onChange={(statusCode: string | null) => {
							if (!canEditSchedule) return;
							if (selectedSectionEvent && statusCode) {
								upsertCell.mutate({
									eventId: selectedSectionEvent.EventId,
									field: "BilletsStatusCode",
									value: statusCode,
									isDraftMode: isDraftModeRef.current,
									defaultAreaId,
								});
								setSectionMenuPosition(null);
								setSelectedSectionEvent(null);
							}
						}}
					/>
				</Box>
			</Popover>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
